import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import logoSmartEfi from "@/assets/logo-smart-efi.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { StepPersonalInfo } from "@/components/auth/StepPersonalInfo";
import { StepOrganisation } from "@/components/auth/StepOrganisation";
import { StepLicence } from "@/components/auth/StepLicence";
import { StepConsents } from "@/components/auth/StepConsents";
import {
  ChevronLeft, ChevronRight, Loader2, Eye, EyeOff, CheckCircle2,
  BarChart3, ShieldCheck, FileText, Zap, ArrowRight,
} from "lucide-react";
import { cn, getErrorMessage } from "@/lib/utils";

// ──────────────────────────────────────────────
// Types & Constants
// ──────────────────────────────────────────────
interface LicenceInfo {
  valid: boolean; reason?: string; licence_id?: string; org_type?: string;
  max_users?: number; max_devices?: number; features?: Record<string, boolean>; valid_until?: string;
}

interface SignupState {
  first_name: string; last_name: string; profession: string; grade: string;
  email: string; country: string; phone: string; password: string;
  org_name: string; org_type: "entreprise" | "cabinet" | "";
  org_address: string; org_email: string; org_website: string; org_siret: string;
  licence_key: string;
  consent_rgpd: boolean; consent_cgu: boolean; consent_droits_auteur: boolean;
}

const INITIAL: SignupState = {
  first_name: "", last_name: "", profession: "", grade: "", email: "",
  country: "", phone: "", password: "",
  org_name: "", org_type: "", org_address: "", org_email: "", org_website: "", org_siret: "",
  licence_key: "", consent_rgpd: false, consent_cgu: false, consent_droits_auteur: false,
};

const STEPS = ["Profil", "Organisation", "Licence", "Consentements"];

const FEATURES = [
  { icon: BarChart3, label: "Montage automatique", sub: "Bilan, CR et TFT générés depuis la balance" },
  { icon: ShieldCheck, label: "Contrôle & pointage", sub: "Vérification automatique de l'équilibre et des écarts" },
  { icon: FileText, label: "Export multi-formats", sub: "PDF, Excel et liasse EFI conformes SYSCOHADA" },
  { icon: Zap, label: "Consolidation", sub: "Périmètre de groupe et éliminations inter-sociétés" },
];

// ──────────────────────────────────────────────
// Constellation particles canvas
// ──────────────────────────────────────────────
function ConstellationCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const PARTICLE_COUNT = 60;
    const CONNECT_DIST = 120;
    const MOUSE = { x: -1000, y: -1000 };

    const resize = () => {
      canvas.width = canvas.offsetWidth * devicePixelRatio;
      canvas.height = canvas.offsetHeight * devicePixelRatio;
      ctx.scale(devicePixelRatio, devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      MOUSE.x = e.clientX - rect.left;
      MOUSE.y = e.clientY - rect.top;
    };
    canvas.addEventListener("mousemove", handleMouseMove);

    interface Particle { x: number; y: number; vx: number; vy: number; r: number; }
    const w = () => canvas.offsetWidth;
    const h = () => canvas.offsetHeight;

    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * w(),
      y: Math.random() * h(),
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.5 + 0.5,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w(), h());

      // Update positions
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w()) p.vx *= -1;
        if (p.y < 0 || p.y > h()) p.vy *= -1;
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECT_DIST) {
            const alpha = (1 - dist / CONNECT_DIST) * 0.15;
            ctx.strokeStyle = `rgba(167,139,250,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }

        // Mouse attraction lines
        const mdx = particles[i].x - MOUSE.x;
        const mdy = particles[i].y - MOUSE.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < 160) {
          const alpha = (1 - mdist / 160) * 0.25;
          ctx.strokeStyle = `rgba(196,181,253,${alpha})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(MOUSE.x, MOUSE.y);
          ctx.stroke();
        }
      }

      // Draw particles
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(196,181,253,0.5)";
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full z-[1]"
      style={{ pointerEvents: "auto" }}
    />
  );
}

// ──────────────────────────────────────────────
// Animated background — left panel
// ──────────────────────────────────────────────
function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0c0a1a] via-[#120e2e] to-[#0a0818]" />

      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.35]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")` }}
      />

      {/* Large animated aurora blobs */}
      <motion.div
        animate={{
          x: [0, 40, -20, 0],
          y: [0, -30, 20, 0],
          scale: [1, 1.15, 0.95, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.22) 0%, rgba(139,92,246,0) 70%)" }}
      />
      <motion.div
        animate={{
          x: [0, -30, 25, 0],
          y: [0, 25, -20, 0],
          scale: [1, 0.9, 1.1, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(236,72,153,0.15) 0%, transparent 70%)" }}
      />
      <motion.div
        animate={{
          x: [0, 20, -15, 0],
          y: [0, -15, 25, 0],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 6 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(79,70,229,0.12) 0%, transparent 65%)" }}
      />

      {/* Fine dot grid */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.5) 0.5px, transparent 0.5px)`,
        backgroundSize: '24px 24px',
      }} />

      {/* Subtle horizontal lines */}
      <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(255,255,255,0.3) 60px)`,
      }} />

      {/* Constellation particles */}
      <ConstellationCanvas />
    </div>
  );
}

// ──────────────────────────────────────────────
// Glassmorphism feature card
// ──────────────────────────────────────────────
function FeatureCard({ icon: Icon, label, sub, index }: { icon: any; label: string; sub: string; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.6 + index * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="group relative"
    >
      <div className="relative rounded-2xl p-4 bg-white/[0.04] border border-white/[0.07] backdrop-blur-md hover:bg-white/[0.07] hover:border-white/[0.12] transition-all duration-500">
        {/* Glow on hover */}
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-violet-500/[0.06] to-transparent pointer-events-none" />
        <div className="relative flex items-start gap-3.5">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-white/[0.08] to-white/[0.03] border border-white/[0.08] flex items-center justify-center group-hover:from-violet-500/20 group-hover:to-indigo-500/10 group-hover:border-violet-400/20 transition-all duration-500">
            <Icon className="w-[18px] h-[18px] text-white/60 group-hover:text-violet-300 transition-colors duration-500" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white/85 leading-tight">{label}</h3>
            <p className="text-[11px] text-white/30 mt-1 leading-relaxed">{sub}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ──────────────────────────────────────────────
// Hero Panel
// ──────────────────────────────────────────────
function HeroPanel() {
  return (
    <div className="hidden lg:flex lg:w-[54%] relative overflow-hidden">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3"
        >
          <img src={logoSmartEfi} alt="Smart-EFI" className="w-10 h-10 rounded-xl object-cover shadow-lg shadow-violet-500/25 ring-1 ring-white/10" />
          <div>
            <span className="text-[15px] font-display font-bold tracking-tight text-white/90">Smart-EFI</span>
            <p className="text-[10px] text-white/30 tracking-[0.15em] uppercase mt-0.5">États Financiers Intelligents</p>
          </div>
        </motion.div>

        {/* Central content */}
        <div className="space-y-8 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full bg-gradient-to-r from-violet-500/[0.12] to-indigo-500/[0.08] border border-violet-400/[0.12]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] text-white/50 font-medium tracking-wide">Conforme SYSCOHADA révisé</span>
            </motion.div>

            <h1 className="text-[2.5rem] xl:text-[2.85rem] font-display font-bold leading-[1.1] tracking-tight">
              <span className="text-white/95">Simplifiez le montage</span>
              <br />
              <span className="text-white/95">de vos </span>
              <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-rose-400 bg-clip-text text-transparent">
                états financiers
              </span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-sm text-white/35 leading-relaxed max-w-md"
          >
            De la balance générale aux états financiers conformes SYSCOHADA,
            automatisez votre production comptable avec précision et fiabilité.
          </motion.p>

          {/* Feature cards */}
          <div className="grid grid-cols-2 gap-2.5">
            {FEATURES.map((f, i) => <FeatureCard key={i} {...f} index={i} />)}
          </div>

          {/* Trust metrics */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.15 }}
            className="flex items-center gap-px"
          >
            {[
              { val: "99.9%", lab: "Disponibilité" },
              { val: "10x", lab: "Plus rapide" },
              { val: "100%", lab: "Conforme OHADA" },
            ].map((s, i) => (
              <div key={i} className={cn(
                "flex-1 text-center py-3 rounded-xl",
                "bg-white/[0.02] border border-white/[0.04]",
                i === 0 && "rounded-r-none border-r-0",
                i === 1 && "rounded-none border-x-0",
                i === 2 && "rounded-l-none border-l-0",
              )}>
                <div className="text-lg font-display font-bold text-white/80">{s.val}</div>
                <div className="text-[10px] text-white/25 uppercase tracking-wider mt-0.5">{s.lab}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.3 }}
          className="flex items-center justify-between text-[10px] text-white/20 tracking-wide"
        >
          <span>© {new Date().getFullYear()} Smart-EFI</span>
          <span className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-white/15" />
            Conforme OHADA · SYSCOHADA révisé
          </span>
        </motion.div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Step indicator (signup)
// ──────────────────────────────────────────────
function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <motion.div
          key={i}
          initial={false}
          animate={{ width: i === current ? 28 : 8 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
          className={cn(
            "h-2 rounded-full transition-colors duration-300",
            i < current
              ? "bg-gradient-to-r from-violet-500 to-indigo-500"
              : i === current
                ? "bg-gradient-to-r from-violet-500 to-indigo-500"
                : "bg-border"
          )}
        />
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────
// Main Auth
// ──────────────────────────────────────────────
export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<SignupState>(INITIAL);
  const [licenceInfo, setLicenceInfo] = useState<LicenceInfo | null>(null);
  const [signupDone, setSignupDone] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") ?? "";
  const redirectTo = searchParams.get("redirect") ?? "/hub";
  const { toast } = useToast();

  const handleFieldChange = useCallback((field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const canProceed = (): boolean => {
    switch (step) {
      case 0: return !!(formData.first_name && formData.last_name && formData.email && formData.profession && formData.country && formData.password.length >= 8);
      case 1: return !!(formData.org_name && formData.org_type);
      case 2: return !!(licenceInfo?.valid && licenceInfo.org_type === formData.org_type);
      case 3: return formData.consent_rgpd && formData.consent_cgu && formData.consent_droits_auteur;
      default: return false;
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.currentTarget);
    const { error } = await supabase.auth.signInWithPassword({
      email: fd.get("email") as string, password: fd.get("password") as string,
    });
    setLoading(false);
    if (error) toast({ title: "Identifiants incorrects ou compte inexistant.", variant: "destructive" });
    else navigate(redirectTo);
  };

  const handleForgotPassword = async (email: string) => {
    if (!email) { toast({ title: "Saisissez votre email d'abord.", variant: "destructive" }); return; }
    await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
    toast({ title: "Email envoyé", description: "Consultez votre boîte mail." });
  };

  const handleSignup = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("register-admin", {
        body: {
          email: formData.email, password: formData.password,
          first_name: formData.first_name, last_name: formData.last_name,
          profession: formData.profession, grade: formData.grade || null,
          country: formData.country, phone: formData.phone,
          org_name: formData.org_name, org_type: formData.org_type,
          org_address: formData.org_address, org_email: formData.org_email,
          org_website: formData.org_website, org_siret: formData.org_siret,
          licence_key: formData.licence_key,
          consent_rgpd: formData.consent_rgpd, consent_cgu: formData.consent_cgu,
          consent_droits_auteur: formData.consent_droits_auteur,
        },
      });
      if (error) throw error;
      if (data.error) {
        const msgs: Record<string, string> = {
          email_already_used: "Cet email est déjà utilisé.",
          invalid_licence: "Licence invalide.",
          licence_org_type_mismatch: `Licence prévue pour "${data.expected}".`,
          consents_required: "Tous les consentements sont obligatoires.",
          missing_required_fields: "Champs obligatoires manquants.",
        };
        throw new Error(msgs[data.error] || data.error);
      }
      setSignupDone(true);
      // Fire-and-forget welcome email (do not block UX on failure)
      supabase.functions.invoke("send-welcome-email", {
        body: {
          email: formData.email,
          first_name: formData.first_name,
          full_name: `${formData.first_name} ${formData.last_name}`.trim(),
          organization_name: formData.org_name,
          role: "admin",
          app_url: window.location.origin,
          context: "signup",
        },
      }).catch((e) => console.warn("welcome email:", e?.message));
    } catch (err: unknown) {
      toast({ title: "Erreur d'inscription", description: getErrorMessage(err), variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <HeroPanel />

      {/* Right — Form panel */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Ambient glow */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-violet-500/[0.03] blur-[120px] -translate-y-1/2 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-indigo-500/[0.02] blur-[100px] translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="w-full max-w-[400px]"
          >
            {/* Mobile branding */}
            <div className="text-center mb-10 lg:hidden">
              <img src={logoSmartEfi} alt="Smart-EFI" className="w-14 h-14 rounded-2xl object-cover shadow-xl shadow-violet-500/20 mx-auto mb-4" />
              <h1 className="text-2xl font-display font-bold text-foreground">Smart-EFI</h1>
              <p className="text-muted-foreground text-sm mt-1">États financiers intelligents</p>
            </div>

            {/* Heading */}
            <div className="hidden lg:block mb-8">
              <h1 className="text-[1.75rem] font-display font-bold tracking-tight text-foreground leading-tight">
                {mode === "login" ? "Bon retour 👋" : "Créer un compte"}
              </h1>
              <p className="text-muted-foreground mt-1.5 text-[14px]">
                {mode === "login"
                  ? "Connectez-vous pour accéder à vos dossiers."
                  : `Étape ${step + 1} sur ${STEPS.length} — ${STEPS[step]}`}
              </p>
            </div>

            {/* Mode toggle */}
            <div className="flex p-1 bg-muted/50 rounded-xl mb-6 border border-border/40">
              {(["login", "signup"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setStep(0); setSignupDone(false); }}
                  className={cn(
                    "flex-1 py-2.5 text-sm font-semibold rounded-[10px] transition-all duration-300 relative",
                    mode === m
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground/70"
                  )}
                >
                  {mode === m && (
                    <motion.div
                      layoutId="auth-tab-bg"
                      className="absolute inset-0 bg-background rounded-[10px] shadow-sm border border-border/50"
                      transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                    />
                  )}
                  <span className="relative z-10">
                    {m === "login" ? "Connexion" : "Inscription"}
                  </span>
                </button>
              ))}
            </div>

            {/* Form container */}
            <AnimatePresence mode="wait">
              <motion.div
                key={mode + (mode === "signup" ? `-${step}` : "")}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
              >
                {mode === "login" ? (
                  <LoginForm
                    loading={loading}
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                    onSubmit={handleLogin}
                    onForgotPassword={handleForgotPassword}
                    initialEmail={prefilledEmail}
                  />
                ) : signupDone ? (
                  <SignupSuccess email={formData.email} />
                ) : (
                  <div className="space-y-5">
                    <StepIndicator current={step} total={STEPS.length} />

                    {step === 0 && (
                      <div className="space-y-4">
                        <StepPersonalInfo data={formData} orgType={formData.org_type || null} onChange={handleFieldChange} />
                        <div className="space-y-1.5">
                          <Label htmlFor="signup-password" className="text-[13px]">
                            Mot de passe <span className="text-destructive">*</span>
                          </Label>
                          <div className="relative">
                            <Input
                              id="signup-password"
                              type={showPassword ? "text" : "password"}
                              value={formData.password}
                              onChange={(e) => handleFieldChange("password", e.target.value)}
                              placeholder="Minimum 8 caractères"
                              className="h-11 rounded-xl bg-muted/30 border-border/50 focus:bg-background transition-colors pr-10"
                              minLength={8}
                            />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          {formData.password.length > 0 && formData.password.length < 8 && (
                            <p className="text-xs text-destructive">Minimum 8 caractères requis</p>
                          )}
                        </div>
                      </div>
                    )}
                    {step === 1 && <StepOrganisation data={formData} onChange={handleFieldChange} />}
                    {step === 2 && (
                      <StepLicence value={formData.licence_key} licenceInfo={licenceInfo}
                        onChange={(v) => handleFieldChange("licence_key", v)} onLicenceValidated={setLicenceInfo} />
                    )}
                    {step === 3 && (
                      <StepConsents
                        data={{ consent_rgpd: formData.consent_rgpd, consent_cgu: formData.consent_cgu, consent_droits_auteur: formData.consent_droits_auteur }}
                        onChange={(field, value) => handleFieldChange(field, value)} />
                    )}

                    <div className="flex gap-3 pt-2">
                      {step > 0 && (
                        <Button type="button" variant="outline" onClick={() => setStep(step - 1)} disabled={loading} className="flex-1 h-11 rounded-xl border-border/50">
                          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
                        </Button>
                      )}
                      {step < STEPS.length - 1 ? (
                        <Button type="button" onClick={() => setStep(step + 1)} disabled={!canProceed()} className="flex-1 h-11 rounded-xl auth-gradient-btn font-semibold text-white">
                          Suivant <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      ) : (
                        <Button type="button" onClick={handleSignup} disabled={!canProceed() || loading} className="flex-1 h-11 rounded-xl auth-gradient-btn font-semibold text-white">
                          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : "Créer mon compte"}
                        </Button>
                      )}
                    </div>

                    <p className="text-center text-xs text-muted-foreground">
                      Invitation reçue ?{" "}
                      <Link to="/join" className="text-primary hover:underline font-semibold">Rejoindre une organisation</Link>
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Bottom trust bar */}
        <div className="py-4 px-6 flex items-center justify-center gap-3 text-[11px] text-muted-foreground/40">
          <span>🔒 Sécurisé</span>
          <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/20" />
          <span>Conforme SYSCOHADA révisé</span>
          <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/20" />
          <span>OHADA</span>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Login Form
// ──────────────────────────────────────────────
function LoginForm({ loading, showPassword, onTogglePassword, onSubmit, onForgotPassword, initialEmail = "" }: {
  loading: boolean; showPassword: boolean; onTogglePassword: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onForgotPassword: (email: string) => void;
  initialEmail?: string;
}) {
  const [email, setEmail] = useState(initialEmail);

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="login-email" className="text-[13px] font-medium">Email</Label>
        <Input
          id="login-email" name="email" type="email" required
          placeholder="vous@exemple.com" value={email}
          onChange={(e) => setEmail(e.target.value)} autoComplete="email"
          className="h-11 rounded-xl bg-muted/30 border-border/50 focus:bg-background transition-colors"
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="login-password" className="text-[13px] font-medium">Mot de passe</Label>
          <button type="button" onClick={() => onForgotPassword(email)}
            className="text-[12px] text-primary hover:text-primary/80 font-semibold transition-colors">
            Mot de passe oublié ?
          </button>
        </div>
        <div className="relative">
          <Input
            id="login-password" name="password" type={showPassword ? "text" : "password"}
            required placeholder="••••••••" autoComplete="current-password"
            className="h-11 rounded-xl bg-muted/30 border-border/50 focus:bg-background transition-colors pr-10"
          />
          <button type="button" onClick={onTogglePassword} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <Button type="submit" disabled={loading}
        className="w-full h-12 rounded-xl auth-gradient-btn text-white font-bold text-sm shadow-xl shadow-violet-500/15 hover:shadow-violet-500/25 transition-shadow duration-300">
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connexion...</>
        ) : (
          <>Se connecter <ArrowRight className="w-4 h-4 ml-2" /></>
        )}
      </Button>
    </form>
  );
}

// ──────────────────────────────────────────────
// Signup success
// ──────────────────────────────────────────────
function SignupSuccess({ email }: { email: string }) {
  return (
    <div className="text-center space-y-5 py-6">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
        className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10"
      >
        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
      </motion.div>
      <div>
        <h3 className="font-display font-bold text-xl text-foreground">Compte créé !</h3>
        <p className="text-muted-foreground text-sm mt-2 leading-relaxed">
          Un email de vérification a été envoyé à<br />
          <span className="font-semibold text-foreground">{email}</span>
        </p>
      </div>
      <Link to="/auth" className="inline-flex items-center gap-1 text-primary text-sm hover:underline font-semibold">
        Retour à la connexion
      </Link>
    </div>
  );
}
